import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, API, TOKEN_KEY, apiError } from "../lib/api";
import {clearUserDrafts} from '../lib/handoverDraft';
import {clearUserOperations} from '../lib/reliableOperations';
import {bindApiSession, identityScope, ACCESS_CHANGED} from '../lib/sessionScope';
import {toast} from 'sonner';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=auth
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const attempt = useRef(0);
  const verified = useRef(null);
  const [sessionVersion, setSessionVersion] = useState(0);
  const acceptUser = useCallback((nextUser, token) => {
    const identity = identityScope(nextUser);
    if (verified.current?.token !== token || verified.current?.identity !== identity) {
      toast.dismiss?.();
      setSessionVersion(value => value + 1);
    }
    verified.current = {token, identity, user:nextUser};
    bindApiSession(token, nextUser);
    setUser(nextUser);
  }, []);

  const invalidateAttempt = useCallback(() => { attempt.current++; }, []);
  const retryAuth = useCallback(async function checkSession(options = {}) {
    const current = ++attempt.current;
    const t = localStorage.getItem(TOKEN_KEY);
    setAuthError("");
    if (!t) {
      acceptUser(false, null);
      setLoading(false);
      return;
    }
    const background = options.background && verified.current?.token === t;
    if (!background) {
      setLoading(true); setUser(null); bindApiSession(null); toast.dismiss?.();
    }
    const isCurrent = () => attempt.current === current && localStorage.getItem(TOKEN_KEY) === t;
    const refreshChangedSession = () => {
      const latest = localStorage.getItem(TOKEN_KEY);
      if (attempt.current === current && latest !== t) {
        void checkSession();
        return true;
      }
      return false;
    };
    try {
      const site = options.site ?? verified.current?.user?.active_site_id;
      const {data} = await api.get("/auth/me", site ? {headers:{"X-Active-Site":site}} : undefined);
      if (refreshChangedSession()) return;
      if (isCurrent()) acceptUser(data, t);
    } catch (error) {
      if (attempt.current !== current) return;
      if (refreshChangedSession()) return;
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (localStorage.getItem(TOKEN_KEY) === t) localStorage.removeItem(TOKEN_KEY);
        acceptUser(false, null);
      } else if (isCurrent()) {
        setUser(null); bindApiSession(null);
        setAuthError("Impossibile verificare la sessione. Controlla la connessione e riprova.");
      }
    } finally {
      if (attempt.current === current) setLoading(false);
    }
  }, [acceptUser]);
  useEffect(() => { retryAuth(); return invalidateAttempt; }, [retryAuth, invalidateAttempt]);
  useEffect(() => {
    const changed = event => {
      if (event.type === 'storage' && event.key !== TOKEN_KEY && event.key !== null) return;
      if (event.type === 'visibilitychange' && document.visibilityState === 'hidden') return;
      if (localStorage.getItem(TOKEN_KEY) !== verified.current?.token) void retryAuth();
      else if (verified.current?.user) void retryAuth({background:true});
    };
    const revoked = () => { void retryAuth(); };
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden' && verified.current?.user) void retryAuth({background:true});
    }, 30000);
    window.addEventListener('storage', changed);
    window.addEventListener('focus', changed);
    window.addEventListener(ACCESS_CHANGED, revoked);
    document.addEventListener('visibilitychange', changed);
    return () => {
      clearInterval(timer);
      window.removeEventListener('storage', changed);
      window.removeEventListener('focus', changed);
      window.removeEventListener(ACCESS_CHANGED, revoked);
      document.removeEventListener('visibilitychange', changed);
    };
  }, [retryAuth]);
  // Resolves with the account the server accepted (null when the check failed).
  const switchSite = useCallback(async site => {
    const token = localStorage.getItem(TOKEN_KEY);
    await retryAuth({site});
    return verified.current?.token === token && localStorage.getItem(TOKEN_KEY) === token ? verified.current.user || null : null;
  }, [retryAuth]);

  const login = async (email, password, tenantId) => {
    const tokenAtStart = localStorage.getItem(TOKEN_KEY);
    const current = ++attempt.current;
    try {
      const { data } = await api.post("/auth/login", { email, password, ...(tenantId ? {tenant_id: tenantId} : {}) });
      if (attempt.current !== current || localStorage.getItem(TOKEN_KEY) !== tokenAtStart) {
        throw new DOMException("Accesso annullato da un’operazione successiva.", "AbortError");
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      acceptUser(data.user, data.token);
      setAuthError("");setLoading(false);
      return data.user;
    } catch (error) {
      // A failed login invalidated the earlier session check: resume it using
      // the token that is current now, unless a later operation already took over.
      if (attempt.current === current) void retryAuth();
      throw error;
    }
  };

  const logout = async () => {
    const current = ++attempt.current;
    const token = localStorage.getItem(TOKEN_KEY);
    const account = verified.current?.token === token ? verified.current.user : null;
    try {
      if (token) await api.post("/auth/logout", null, {headers: {Authorization: `Bearer ${token}`}});
    } catch (e) { /* the selected local session can still be closed */ }
    if (attempt.current !== current) return;
    if (localStorage.getItem(TOKEN_KEY) !== token) {
      void retryAuth();
      return;
    }
    localStorage.removeItem(TOKEN_KEY);
    // Drafts can hold customer signatures: nothing of this account stays behind.
    if (account) {
      try { clearUserDrafts(localStorage, API, account); clearUserOperations(API, account); }
      catch (e) { /* storage unavailable: nothing more can be removed */ }
    }
    acceptUser(false, null);
    setAuthError("");setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, authError, retryAuth, switchSite, login, logout, setUser,
      sessionKey: `${identityScope(user)}:${sessionVersion}` }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
